import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpContext } from '@angular/common/http';
import {
  AuthLoginDto,
  AuthLoginResponseDto,
  AuthLogoutResponseDto,
  AuthRefreshResponseDto,
  AuthService,
} from '../api';
import { UserAccountDetailDto } from '../api/model/userAccountDetailDto';
import { Router } from '@angular/router';
import { BehaviorSubject, catchError, EMPTY, map, Observable, of, tap } from 'rxjs';
import { UserRolesService } from '../api/api/userRoles.service';
import { FORCE_USER_TOKEN } from '../http-context/force-user-token.context';

const USER_STORAGE_KEY = 'auth_user';
const ROLES_STORAGE_KEY = 'auth_roles';
const IMPERSONATION_TOKEN_KEY = 'impersonation_token';

/**
 * Roles that use the principal area. An Administrator (school administrator)
 * has exactly a principal's access for now — only the title differs.
 */
const PRINCIPAL_ACCESS_ROLES = ['principal', 'administrator'];

export const rolesHavePrincipalAccess = (roles: readonly string[]): boolean =>
  roles.some((r) => PRINCIPAL_ACCESS_ROLES.includes(r.toLowerCase()));

@Injectable({
  providedIn: 'root',
})
export class AuthSessionService {
  private accessToken = signal<string | null>(sessionStorage.getItem(IMPERSONATION_TOKEN_KEY));
  private currentUser = signal<UserAccountDetailDto | null>(this.loadUserFromStorage());
  private userRoles = signal<string[]>(this.loadRolesFromStorage());

  isRefreshing = false;
  refreshSubject = new BehaviorSubject<string | null>(null);

  private authService = inject(AuthService);
  private userRolesService = inject(UserRolesService);
  private router = inject(Router);

  readonly user = this.currentUser.asReadonly();
  readonly roles = this.userRoles.asReadonly();
  readonly isAuthenticated = computed(() => !!this.accessToken());
  /** Principal or Administrator — both get the full principal area. */
  readonly hasPrincipalAccess = computed(() => rolesHavePrincipalAccess(this.userRoles()));
  /** Titled "Administrator" rather than "Principal" (same access). */
  readonly isAdministrator = computed(() => {
    const roles = this.userRoles().map((r) => r.toLowerCase());
    return roles.includes('administrator') && !roles.includes('principal');
  });
  /** Only the Principal role can grant or remove administrator access. */
  readonly canManageAdministrators = computed(() =>
    this.userRoles().some((r) => r.toLowerCase() === 'principal')
  );
  readonly isTeacher = computed(() =>
    this.userRoles().some((r) => r.toLowerCase().includes('teacher'))
  );

  private loadUserFromStorage(): UserAccountDetailDto | null {
    try {
      const raw = localStorage.getItem(USER_STORAGE_KEY);
      return raw ? (JSON.parse(raw) as UserAccountDetailDto) : null;
    } catch {
      return null;
    }
  }

  private loadRolesFromStorage(): string[] {
    try {
      const raw = localStorage.getItem(ROLES_STORAGE_KEY);
      return raw ? (JSON.parse(raw) as string[]) : [];
    } catch {
      return [];
    }
  }

  login(dto: AuthLoginDto): Observable<string[]> {
    return this.authService.authControllerLogin(dto).pipe(
      tap((response: AuthLoginResponseDto) => {
        this.accessToken.set(response.accessToken);
        this.currentUser.set(response.user as UserAccountDetailDto);
        localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(response.user));
        const roles = (response.roles ?? []).map((r) => r.toLowerCase());
        this.userRoles.set(roles);
        localStorage.setItem(ROLES_STORAGE_KEY, JSON.stringify(roles));
      }),
      map((response: AuthLoginResponseDto) => (response.roles ?? []).map((r) => r.toLowerCase()))
    );
  }

  fetchAndStoreRoles(): Observable<string[]> {
    // Always the current school-user's own roles — never the admin's — so
    // force the user/impersonation token even if still physically on an
    // `/admin/*` page (e.g. immediately after starting an impersonation,
    // before the follow-up navigation to `/principal/*` has happened). See
    // FORCE_USER_TOKEN's own doc for why this matters.
    const context = new HttpContext().set(FORCE_USER_TOKEN, true);
    return this.userRolesService.userRoleControllerGetMyRoles('body', false, { context }).pipe(
      tap((roleNames: string[]) => {
        const lower = roleNames.map((r) => r.toLowerCase());
        this.userRoles.set(lower);
        localStorage.setItem(ROLES_STORAGE_KEY, JSON.stringify(lower));
      }),
      catchError(() => {
        return of(this.userRoles());
      })
    );
  }

  refresh(): Observable<AuthRefreshResponseDto> {
    // During impersonation the access token is restored from sessionStorage on
    // init — using the admin's refresh cookie here would overwrite it with an
    // admin token, breaking the impersonated session.
    if (this.isImpersonating) {
      return of({ accessToken: this.accessToken()! } as AuthRefreshResponseDto);
    }
    return this.authService.authControllerRefresh().pipe(
      tap((response: AuthRefreshResponseDto) => {
        this.accessToken.set(response.accessToken);
      })
    );
  }

  logout(): Observable<AuthLogoutResponseDto> {
    // Signing out of an impersonated session should return the admin to the
    // admin panel (their own login survives — see clearSession()), not drop
    // them on the school-user login screen they never actually use.
    const returnTo =
      this.isImpersonating && localStorage.getItem('admin_access_token')
        ? '/admin/dashboard'
        : '/login';

    return this.authService.authControllerLogout().pipe(
      tap(() => {
        this.clearSession();
        this.router.navigate([returnTo]);
      }),
      catchError(() => {
        this.clearSession();
        this.router.navigate([returnTo]);
        return EMPTY;
      })
    );
  }

  getAccessToken(): string | null {
    return this.accessToken();
  }

  updateUser(user: UserAccountDetailDto): void {
    this.currentUser.set(user);
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
  }

  /** Used by admins to impersonate a user account without requiring login credentials. */
  setSession(user: UserAccountDetailDto, accessToken: string): void {
    this.accessToken.set(accessToken);
    this.currentUser.set(user);
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
    sessionStorage.setItem(IMPERSONATION_TOKEN_KEY, accessToken);
  }

  get isImpersonating(): boolean {
    return !!sessionStorage.getItem(IMPERSONATION_TOKEN_KEY);
  }

  resetRefreshOnFailure(error: unknown): void {
    this.isRefreshing = false;
    const prev = this.refreshSubject;
    this.refreshSubject = new BehaviorSubject<string | null>(null);
    prev.error(error);
  }

  private clearSession() {
    this.accessToken.set(null);
    this.currentUser.set(null);
    this.userRoles.set([]);
    localStorage.removeItem(USER_STORAGE_KEY);
    localStorage.removeItem(ROLES_STORAGE_KEY);
    // Deliberately NOT clearing 'admin_access_token' here: this method also
    // runs when signing out of an impersonated session, and the admin's own
    // login must survive that so they land back in the admin panel rather
    // than being logged out of admin entirely (see the impersonation note on
    // `refresh()` above). The admin panel's own sign-out
    // (admin.component.ts) clears its token independently.
    sessionStorage.removeItem(IMPERSONATION_TOKEN_KEY);
    this.isRefreshing = false;
    const prev = this.refreshSubject;
    this.refreshSubject = new BehaviorSubject<string | null>(null);
    prev.error(new Error('Session cleared'));
  }
}
